import React, { useState, useEffect, useRef } from 'react';
import ListPageHeader from '../common/ListPageHeader.js';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Alert from '../common/Alert.js';
import roleService from '../../services/RoleService.js';
import { Plus, Settings  } from "lucide-react";
import Button from '../common/Button.js';
import RowActionsMenu from '../common/RowActionsMenu.js';
import { useTranslation } from 'react-i18next';
import { formatAuditUser } from '../../utils/userDisplay.js';


const RoleList = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await roleService.getAll();
      setRoles(response.data || []);
    } catch (err) {
      Alert.error('Failed to load roles');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (roleId) => {
    const confirmed = await Alert.confirm(
      'Are you sure you want to delete this role? This action cannot be undone.',
      'Delete Role'
    );
    
    if (confirmed) {
      try {
        await roleService.delete(roleId);
        Alert.success('Role deleted successfully!');
        fetchRoles();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete role');
      }
    }
  };

  const toggleDropdown = (roleId) => {
    setActiveDropdown(activeDropdown === roleId ? null : roleId);
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        <ListPageHeader
          title="Roles"
          icon={Settings}
          titleClassName="text-secondary"
          iconClassName="w-5 h-5 text-buttonBg shrink-0"
          className="mb-4 sm:mb-4"
        >
          <Button mobileIconOnly icon={Plus} onClick={() => navigate('/users/add')}>
            Add User
          </Button>
          <Button mobileIconOnly icon={Plus} onClick={() => navigate('/roles/add')}>
            Add Roles
          </Button>
        </ListPageHeader>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm">
          {loading ? (
            <div className="p-6 sm:p-8 text-center text-gray-600">
              <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
              <p className="text-sm sm:text-base">Loading roles...</p>
            </div>
          ) : roles.length === 0 ? (
            <div className="p-6 sm:p-8 text-center text-sm sm:text-base text-gray-600">No roles found</div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden lg:block w-full">
                <table className="w-full table-fixed border-collapse">
                  <thead>
                    <tr className="bg-primary text-tertiary">
                      <th className="px-4 lg:px-6 py-3 lg:py-4 text-left text-xs lg:text-sm font-semibold">ROLE NAME</th>
                      <th className="px-4 lg:px-6 py-3 lg:py-4 text-left text-xs lg:text-sm font-semibold">CREATED BY</th>
                      <th className="px-4 lg:px-6 py-3 lg:py-4 text-left text-xs lg:text-sm font-semibold">CREATED DATE</th>
                      <th className="px-4 lg:px-6 py-3 lg:py-4 text-left text-xs lg:text-sm font-semibold">LAST UPDATED</th>
                      <th className="px-4 lg:px-6 py-3 lg:py-4 text-left text-xs lg:text-sm font-semibold">ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {roles.map((role) => (
                      <tr key={role.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 lg:px-6 py-2 lg:py-2">
                          <button
                            onClick={() => navigate(`/roles/view/${role.id}`)}
                            className="font-medium text-secondary hover:text-secondary-700 hover:underline text-sm lg:text-base"
                          >
                            {role.role_name}
                          </button>
                        </td>
                        <td className="px-4 lg:px-6 py-2 lg:py-2 text-gray-700 text-sm lg:text-base">{role.created_by || role.created_by_display ? formatAuditUser(role, 'created_by') : 'System'}</td>
                        <td className="px-4 lg:px-6 py-2 lg:py-2 text-gray-700 text-sm lg:text-base">
                          {new Date(role.created_at).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </td>
                        <td className="px-4 lg:px-6 py-2 lg:py-2 text-gray-700 text-sm lg:text-base">
                          {new Date(role.updated_at).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </td>
                        <td className="px-4 lg:px-6 py-2 lg:py-2 relative">
                          <RowActionsMenu
                            isOpen={activeDropdown === role.id}
                            onToggle={() => toggleDropdown(role.id)}
                            onClose={() => setActiveDropdown(null)}
                            menuRef={dropdownRef}
                            onView={() => navigate(`/roles/view/${role.id}`)}
                            onEdit={() => navigate(`/roles/edit/${role.id}`)}
                            onDelete={() => handleDelete(role.id)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden divide-y divide-gray-200">
                {roles.map((role) => (
                  <div key={role.id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between mb-3">
                      <button
                        onClick={() => navigate(`/roles/view/${role.id}`)}
                        className="font-semibold text-orange-600 hover:text-orange-700 hover:underline text-base"
                      >
                        {role.role_name}
                      </button>
                      <RowActionsMenu
                        isOpen={activeDropdown === role.id}
                        onToggle={() => toggleDropdown(role.id)}
                        onClose={() => setActiveDropdown(null)}
                        menuRef={dropdownRef}
                        onView={() => navigate(`/roles/view/${role.id}`)}
                        onEdit={() => navigate(`/roles/edit/${role.id}`)}
                        onDelete={() => handleDelete(role.id)}
                        className="relative flex-shrink-0"
                      />
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Created By:</span>
                        <span className="text-gray-900 font-medium">{role.created_by || role.created_by_display ? formatAuditUser(role, 'created_by') : 'System'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Created:</span>
                        <span className="text-gray-900 font-medium">
                          {new Date(role.created_at).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Updated:</span>
                        <span className="text-gray-900 font-medium">
                          {new Date(role.updated_at).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Bottom Info */}
              <div className="bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 border-t">
                <div className="text-xs sm:text-sm text-gray-700">
                  Showing {roles.length} role{roles.length !== 1 ? 's' : ''}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default RoleList;
